interface AuthSession {
  organizations?: { id: string; slug: string }[];
  shops?: { id: string; domain: string; organizationId?: string }[];
  currentOrganization?: { id: string } | null;
  currentShop?: { id: string } | null;
}
interface Preference {
  organizationId: string | null;
  organizationSlug: string | null;
  shopId: string | null;
  shopDomain: string | null;
}

/** Resolve both warehouse choices and permissions from the same server context. */
export async function resolveAuthSelection<T extends AuthSession>(
  initial: T, preference: Preference, read: (headers: Record<string, string>) => Promise<T>,
) {
  const organizations = initial.organizations || [];
  const organization = organizations.find(o => o.slug === preference.organizationSlug)
    || organizations.find(o => o.id === preference.organizationId)
    || organizations.find(o => o.id === initial.currentOrganization?.id)
    || organizations[0];
  const headers: Record<string, string> = organization ? { 'X-Organization-Id': organization.id } : {};
  let data = initial;
  if (organization && data.currentOrganization?.id !== organization.id) data = await read(headers);
  if ((data.currentOrganization?.id || null) !== (organization?.id || null)) throw new Error('Company selection changed. Refresh and choose the company again.');
  const shops = (data.shops || []).filter(s => !s.organizationId || s.organizationId === organization?.id);
  const shop = shops.find(s => s.domain === preference.shopDomain)
    || shops.find(s => s.id === preference.shopId)
    || shops.find(s => s.id === data.currentShop?.id) || shops[0];
  if (shop && data.currentShop?.id !== shop.id) data = await read({ ...headers, 'X-Shop-Domain': shop.domain });
  if ((data.currentOrganization?.id || null) !== (organization?.id || null)
    || (data.currentShop?.id || null) !== (shop?.id || null)) throw new Error('Warehouse selection changed. Refresh and choose the warehouse again.');
  return { data, organizationId: organization?.id || null, shopId: shop?.id || null };
}
